import bilAPI from "./api";

// Subject variants (BTS terms of reference 4.1): the questions of a subject folder for one grade, composed by the
// subject's matrix. Only the subject's moderator and admins (others: 403; another subject's moderator: 404).
// A variant: {uuid, name, heading, description, folder: {uuid, name, stream, parent: {uuid, name}}, subject, grade,
// status: draft | confirmed, confirmed_by, confirmed_at, revision, questions_count, points_total,
// calculator_is_allowed, periodic_table_is_allowed, additional_information, keep_order: [question uuid],
// booklets: [{uuid, name, status}], locked (in an active booklet: read only), created, updated}.
// The detail adds `test_questions` ([{uuid, order, points, question: full question}], in the variant's order) and
// `check`. A question the user no longer reads (moved since to a folder not given to them) comes as
// {uuid, code, passage: {uuid} | null, readable: false}. Every write may send `revision` (the detail's): a variant
// changed since answers 409 variant_changed.
// A matrix row comes as a row_ref (matrix v2): {uuid, grade, topic: {uuid, name, grade, code} | null, subtopics:
// [{uuid, name}] ([] with a topic: the whole topic), quarter, week, hours}, in check.rows[].row, the issues, propose's
// shortfalls, a 400 slot_mismatch and candidates' row. Its uuid changes with every save of the matrix. A row without a
// topic can't be filled: the check gives it a blocking row_without_topic per planned level (no confirm, no active
// booklet until the matrix names its topic).
const json = { headers: { "Content-Type": "application/json" } };
const base = "/exams/subject-variants/";

// params: folder (a subject, or an exam folder for all its subjects), grade, status, page, page_size (at most 100:
// the server cuts a bigger page down to 100 and says nothing, so a caller that wants more turns the pages).
export const getSubjectVariants = ({ params, signal } = {}) => bilAPI.get(base, { params, signal }).then((res) => res.data);

export const getSubjectVariant = (uuid, { signal } = {}) => bilAPI.get(`${base}${uuid}/`, { signal }).then((res) => res.data);

// data: {folder: subject uuid, grade: 1–12, name?, heading?, description?} → 201 the detail (a draft). 400 not_subject_folder.
export const createSubjectVariant = (data) => bilAPI.post(base, data, json).then((res) => res.data);

// data: {name, heading, description, calculator_is_allowed, periodic_table_is_allowed, additional_information,
// keep_order: [question uuid], revision?} → the detail. A change of what prints (all but name and description)
// takes the confirmation off.
export const updateSubjectVariant = (uuid, data) => bilAPI.patch(`${base}${uuid}/`, data, json).then((res) => res.data);

// 409 variant_in_booklet while a booklet (any) holds it.
export const deleteSubjectVariant = (uuid) => bilAPI.delete(`${base}${uuid}/`).then((res) => res.data);

// The composition writes, each answering with the detail:
// - propose {seed?, replace?, revision?}: the system's proposal by the matrix; the answer adds {seed, shortfalls:
//   [{row, level, planned, proposed, available}] (a row without a topic: available 0), reused: [question uuid]}.
//   409 variant_not_empty without `replace`; 400 matrix_without_topics when no row of the grade with a topic plans
//   anything;
// - add {question}, remove {test_question}, replace {test_question, question} (the row keeps its uuid and number);
// - add_passage {passage}, remove_passage {passage}: a passage goes in and out whole;
// - confirm {revision?} (400 variant_incomplete {check}), unconfirm {reason?}.
const variantAction = (action) => (uuid, body = {}) =>
  bilAPI.post(`${base}${uuid}/${action}/`, body, json).then((res) => res.data);

export const proposeVariant = variantAction("propose");
export const addToVariant = variantAction("add");
export const removeFromVariant = variantAction("remove");
export const replaceInVariant = variantAction("replace");
export const addPassageToVariant = variantAction("add_passage");
export const removePassageFromVariant = variantAction("remove_passage");
export const confirmVariant = variantAction("confirm");
export const unconfirmVariant = variantAction("unconfirm");

// The approved questions that may take a place, not in the variant yet (up to 50, those in fewer other tests
// first): params {test_question} (that row's place, first if both come) or {row: the row's uuid, level}, and
// {search}. → {row, level, questions: [full question + uses]}; row and level are null for a place outside the
// matrix (or without `level`), and a row without a topic has no questions. 400 row_not_found: the row is no longer
// in the matrix (saved meanwhile: read the variant again); 400 stale_client: asked the pre-v2 way ({topic,
// subtopic, level}, which only a pre-v2 server's rows without a uuid still need).
export const getVariantCandidates = (uuid, { params, signal } = {}) =>
  bilAPI.get(`${base}${uuid}/candidates/`, { params, signal }).then((res) => res.data);

// {ok, grade, planned, actual, standard, formats, rows: [{row, planned, actual, approved, test_questions: {A, B, C:
// [test question uuid]}}], issues, changed_since_confirmation}: also in the detail.
export const getVariantCheck = (uuid, { signal } = {}) => bilAPI.get(`${base}${uuid}/check/`, { signal }).then((res) => res.data);

// The variant's rows ([{uuid, order, points, question}]): also in the detail.
export const getVariantQuestions = (uuid, { signal } = {}) =>
  bilAPI.get(`${base}${uuid}/questions/`, { signal }).then((res) => res.data);
